import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import type { AxiosRequestConfig } from 'axios'
import { useAuthStore } from './auth'
import { ApiClient } from '@/utils/api-client'
import { ENDPOINTS, type ApiResponseTypeV3 } from '@/api'
import type { NotificationItem } from '@/queries/notifications/types'
import { normalizeNotification } from '@/utils/notification-target'

// Optional notification requests show their own errors instead of redirecting the page.
const requestConfig: AxiosRequestConfig & { suppressGlobalErrorRedirects: boolean } =
  {
    suppressGlobalErrorRedirects: true
  }

export const useNotificationStore = defineStore('notifications', () => {
  const auth = useAuthStore()
  const accountId = computed(() =>
    auth.isAuthenticated ? (auth.user?.id ?? '') : ''
  )
  const items = ref<NotificationItem[]>([])
  const unreadCount = ref(0)
  const isLoading = ref(false)
  const error = ref('')
  const hasMore = ref(false)
  let accountVersion = 0
  let countVersion = 0

  watch(
    accountId,
    () => {
      accountVersion++
      countVersion++
      items.value = []
      unreadCount.value = 0
      isLoading.value = false
      error.value = ''
      hasMore.value = false
    },
    { flush: 'sync' }
  )

  async function refreshUnreadCount() {
    if (!accountId.value) return
    const version = accountVersion
    const request = ++countVersion
    try {
      const response = await ApiClient.get<{ data: { count: number } }>(
        ENDPOINTS.Notifications.UnreadCount,
        requestConfig
      )
      if (version !== accountVersion || request !== countVersion) return
      const count = Number(response.data?.count)
      if (Number.isFinite(count)) unreadCount.value = Math.max(0, Math.floor(count))
    } catch {
      // A badge failure must not erase the inbox or interrupt the current page.
    }
  }

  async function loadNotifications() {
    if (!accountId.value || isLoading.value) return
    const version = accountVersion
    isLoading.value = true
    error.value = ''
    try {
      // The API consumes returned unread records. Page 2 would skip the next batch.
      const response = await ApiClient.get<ApiResponseTypeV3<unknown[]>>(
        ENDPOINTS.Notifications.List,
        { ...requestConfig, params: { page: 1 } }
      )
      if (version !== accountVersion) return
      if (!Array.isArray(response.data))
        throw new Error('Invalid notification response')
      const incoming = response.data
        .map(normalizeNotification)
        .filter((item): item is NotificationItem => item !== null)
      const unique = new Map(items.value.map((item) => [item.id, item]))
      incoming.forEach((item) => unique.set(item.id, item))
      items.value = Array.from(unique.values()).sort(
        (a, b) => (Date.parse(b.createdAt) || 0) - (Date.parse(a.createdAt) || 0)
      )
      hasMore.value = response.data.length > 0 && response.meta?.next_page != null
      void refreshUnreadCount()
    } catch {
      if (version === accountVersion)
        error.value = 'دریافت اعلان‌ها انجام نشد. دوباره تلاش کنید.'
    } finally {
      if (version === accountVersion) isLoading.value = false
    }
  }

  return {
    accountId,
    items,
    unreadCount,
    isLoading,
    error,
    hasMore,
    loadNotifications,
    refreshUnreadCount
  }
})
